import { ArrowRightIcon } from '@eleveo/icons-react';
import { Button, Col, Container, Icon, Row } from '@zoom/components';
import React from 'react';
import { FormRenderProps, FormSpy } from 'react-final-form';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Form } from 'reactstrap';

import { LoadingOverlay } from '../../../components/LoadingOverlay';
import { PromptModal } from '../../../components/Prompt/PromptModal';
import { Content } from '../../../components/layout/Content';
import { Separator } from '../../../components/layout/Separator';
import { Queue, QueueHistoryRangeComponent } from '../../../graphql/components';
import { CreateEditViewType as Action } from '../../../types';
import { Adjustment } from '../config';
import { getButtonTestId, SAVE_BUTTON, NEXT_STEP_BUTTON, CANCEL_BUTTON, SKIP_SWITCH_TO_EDIT } from '../constants';
import { getHeaderData } from '../utils/header';
import { FormValues, FormSpyValues } from '../utils/values';
import { FormForecastBase } from './FormForecastBase';
import { FormForecastRange } from './FormForecastRange';

type TFormCreateForecastProps = FormRenderProps<FormValues> &
  WithTranslation & {
    action: Action;
    adjustments: Adjustment[];
    updateAdjustments: (adjustments: Adjustment[]) => void;
    isEdit: boolean;
    isProduction: boolean;
    isView: boolean;
    onCancel: () => void;
    onChange: (props: FormSpyValues) => void;
    onNextStep: (values: FormValues) => void;
    queues: Queue[];
    queueTimezone: string;
    shouldBlockLeave: boolean;
  };

const FormCreateForecastComponent: React.FC<TFormCreateForecastProps> = (props) => {
  const {
    action,
    adjustments,
    updateAdjustments,
    form,
    handleSubmit,
    invalid,
    isEdit,
    isProduction,
    isView,
    onCancel,
    onChange,
    onNextStep,
    queues,
    shouldBlockLeave,
    t,
    values,
  } = props;

  const { testId, title, titleSlug } = getHeaderData(isEdit, isView);

  const headerProps = {
    testId,
    titleSlug,
    actionBar: (
      <Row>
        <Col xs="auto">
          <Button
            color="primary"
            disabled={invalid}
            data-test-id={getButtonTestId(action, 1, NEXT_STEP_BUTTON)}
            kind="icon"
            onClick={() => onNextStep(values)}
            title={t('layout.steps.next')}
          >
            <ArrowRightIcon className="text-white" />
          </Button>
        </Col>
        <Separator />
        {!isView && (
          <Col xs="auto">
            <Button
              color="secondary"
              data-test-id={getButtonTestId(action, 1, SAVE_BUTTON)}
              disabled={invalid || isProduction}
              type="submit"
            >
              {t('layout.action.save')}
            </Button>
          </Col>
        )}
        <Col xs="auto">
          <Button
            className="button--icon--left-hand-side"
            color="info"
            data-test-id={getButtonTestId(action, 1, CANCEL_BUTTON)}
            onClick={onCancel}
          >
            <Icon color="#eb651c" kind="cross" size={10} />
            {t('layout.action.cancel')}
          </Button>
        </Col>
      </Row>
    ),
    title: t(title),
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormSpy<FormValues>
        subscription={{ dirty: true, modified: true, valid: true, values: true }}
        onChange={({ dirty, modified, valid, values: onChangeValues }) => {
          // Do not fire on initial render, when updating state is causing React error
          if (modified && Object.values(modified).filter((filteredValueModified) => filteredValueModified).length) {
            onChange({ dirty, valid, values: onChangeValues });
          }
        }}
      />
      <PromptModal
        t={t}
        when={shouldBlockLeave}
        onSave={async ({ proceed, close }) => {
          try {
            form.change(SKIP_SWITCH_TO_EDIT, true);
            await handleSubmit();
            proceed();
          } catch (e) {
            close();
          }
        }}
      />
      <Content header={headerProps}>
        <Container className="forecasting-studio__create-forecast ">
          <FormForecastBase
            form={form}
            isEdit={isEdit}
            isProduction={isProduction}
            isView={isView}
            queues={queues}
            t={t}
          />
          <QueueHistoryRangeComponent
            fetchPolicy="network-only"
            variables={{ queueId: values.queueId }}
            skip={!values.queueId}
          >
            {(queueHistoryRange) => (
              <LoadingOverlay loading={queueHistoryRange.loading}>
                <FormForecastRange
                  {...props}
                  adjustments={adjustments}
                  updateAdjustments={updateAdjustments}
                  queueHistoryRange={queueHistoryRange}
                  values={values}
                  form={form}
                />
              </LoadingOverlay>
            )}
          </QueueHistoryRangeComponent>
        </Container>
      </Content>
    </Form>
  );
};

export const FormCreateForecast = withTranslation()(FormCreateForecastComponent);
